import { Injectable } from '@angular/core';
import { FidelityPageService } from 'app/core/services/fidelity-page.service';
import { catchError, finalize, Observable, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiResult } from '../../../shared/types/api-result.types';
import {
    BaseDataForUpdatePersonalDto,
    FiscalDataForUpdatePersonalDto,
    GetFidelityDataResponse,
    GetFidelityTransactionsDataResponse,
    GetFiscalDataResponse,
    GetPersonalDataResponse,
    UpdatePasswordDto,
    UpdatePersonalConsentsDto,
    UpdatePersonalDataDto,
} from './personal-data.types';

@Injectable({
    providedIn: 'root',
})
export class FidelityPersonalDataService extends FidelityPageService {
    private fidelityInfoCacheKey = `${FidelityPersonalDataService.name}-fidelityInfo`;
    private personalDataCacheKey = `${FidelityPersonalDataService.name}-personalData`;
    private fiscalDataCacheKey = `${FidelityPersonalDataService.name}-fiscalData`;

    getFidelityInfo(): Observable<ApiResult<GetFidelityDataResponse>> {
        if (this.memoryCacheService.has(this.fidelityInfoCacheKey)) {
            return of(this.memoryCacheService.get(this.fidelityInfoCacheKey));
        }

        this.fetchingData.set(true);
        return this.http.get<ApiResult<GetFidelityDataResponse>>(`${environment.apiUrl}/personal/fidelity`).pipe(
            catchError(this.handleError),
            tap((res) => {
                this.memoryCacheService.set(this.fidelityInfoCacheKey, res);
            }),
            finalize(() => this.fetchingData.set(false))
        );
    }

    getFidelityTransactions() {
        this.fetchingData.set(true);
        return this.http
            .get<ApiResult<GetFidelityTransactionsDataResponse>>(`${environment.apiUrl}/personal/fidelity/transactions`)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.fetchingData.set(false))
            );
    }

    getPersonalData(): Observable<ApiResult<GetPersonalDataResponse>> {
        if (this.memoryCacheService.has(this.personalDataCacheKey)) {
            return of(this.memoryCacheService.get(this.personalDataCacheKey));
        }

        this.fetchingData.set(true);
        return this.http.get<ApiResult<GetPersonalDataResponse>>(`${environment.apiUrl}/personal/`).pipe(
            catchError(this.handleError),
            tap((res) => {
                this.memoryCacheService.set(this.personalDataCacheKey, res);
            }),
            finalize(() => this.fetchingData.set(false))
        );
    }

    getFiscalData(): Observable<ApiResult<GetFiscalDataResponse>> {
        if (this.memoryCacheService.has(this.fiscalDataCacheKey)) {
            return of(this.memoryCacheService.get(this.fiscalDataCacheKey));
        }

        this.fetchingData.set(true);
        return this.http.get<ApiResult<GetFiscalDataResponse>>(`${environment.apiUrl}/personal/fiscal`).pipe(
            catchError(this.handleError),
            tap((res) => {
                this.memoryCacheService.set(this.fiscalDataCacheKey, res);
            }),
            finalize(() => this.fetchingData.set(false))
        );
    }

    private updatePersonalDataCore(dto: UpdatePersonalDataDto): Observable<ApiResult<never>> {
        this.disableCrudActions.set(true);

        return this.http.post<ApiResult<never>>(`${environment.apiUrl}/personal/update`, dto).pipe(
            catchError(this.handleError),
            finalize(() => this.disableCrudActions.set(false))
        );
    }

    updatePersonalData(dto: BaseDataForUpdatePersonalDto): Observable<ApiResult<never>> {
        this.memoryCacheService.delete(this.personalDataCacheKey);

        return this.updatePersonalDataCore({ personal: dto });
    }

    updateFiscalData(dto: FiscalDataForUpdatePersonalDto): Observable<ApiResult<never>> {
        this.memoryCacheService.delete(this.fiscalDataCacheKey);

        return this.updatePersonalDataCore({ fiscal: dto });
    }

    updateConsents(dto: UpdatePersonalConsentsDto): Observable<ApiResult<never>> {
        this.disableCrudActions.set(true);
        this.memoryCacheService.delete(this.personalDataCacheKey);

        return this.http.post<ApiResult<never>>(`${environment.apiUrl}/personal/consents/update`, dto).pipe(
            catchError(this.handleError),
            finalize(() => this.disableCrudActions.set(false))
        );
    }

    deletePersonalData() {
        this.disableCrudActions.set(true);
        return this.http.delete<ApiResult<never>>(`${environment.apiUrl}/personal/delete`).pipe(
            catchError(this.handleError),
            tap((_) => this.memoryCacheService.clear()),
            finalize(() => this.disableCrudActions.set(false))
        );
    }

    updatePassword(dto: UpdatePasswordDto) {
        this.disableCrudActions.set(true);
        return this.http.post(`${environment.apiUrl}/personal/password`, dto).pipe(
            catchError(this.handleError),
            finalize(() => this.disableCrudActions.set(false))
        );
    }
}
